import { FC, useMemo, useCallback, useState, useEffect } from "react"
import axios from "axios"
import { withPrefix } from "gatsby"

// components
import { Icon } from "../Icon/Icon"

// icons
import { CloseIcon, Typography } from "@eventbrite/marmalade"

// utils
import { renderRichText } from "../../utils/contentful/render_rich_text"
import { events } from "../../utils/heap/tracking_events"
import { extractLangCode } from "../../utils/general_utils"

// styles
import * as styles from "./Banner.module.scss"
import {
  enhancedCustomRendererToRemovePTag,
  renderEnhancedRichText
} from "../../utils/contentful/enhanced-rich-text-renderer"

// types
import {
  ApiBanner,
  ApiBannerResponse
} from "../../../backend/amplify/functions/api-functions/FetchSiteBanner/handler"

type ContentfulRichTextRaw = { raw?: string }

type BannerMessage = {
  callToAction?: ContentfulRichTextRaw
  siteBannerMessage?: ContentfulRichTextRaw
  siteBannerHeader?: string
}
type FinalBannerMessage = BannerMessage & { key: string }

const toRichTextRaw = (value?: object): ContentfulRichTextRaw | undefined => {
  if (!value) return undefined
  if (
    "raw" in value &&
    typeof (value as ContentfulRichTextRaw).raw === "string"
  ) {
    return value as ContentfulRichTextRaw
  }
  return { raw: JSON.stringify(value) }
}

const mapApiBannersToFinalBannerMessages = (
  banners: ApiBanner[]
): FinalBannerMessage[][] => {
  const mapped = (banners ?? [])
    .map((banner) => {
      const siteBannerMessage = toRichTextRaw(banner.siteBannerMessage)
      const rendered = renderRichText(siteBannerMessage?.raw)
      const key = rendered
        ? (rendered as any)[0]?.props?.children?.[0]
        : siteBannerMessage?.raw?.slice(0, 30)

      return {
        callToAction: toRichTextRaw(banner.callToAction),
        siteBannerMessage,
        siteBannerHeader: banner.siteBannerHeader,
        key: key ?? banner.siteBannerInternalTitle ?? ""
      }
    })
    .filter((b) => b)

  return mapped.length > 0 ? [mapped] : []
}

export const Banner: FC = () => {
  const langCode = useMemo(() => extractLangCode(), [])
  const [bannerMessages, setBannerMessages] = useState<FinalBannerMessage[]>([])
  const [hasFetched, setHasFetched] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchBanners = async () => {
      try {
        const response = await axios.get<ApiBannerResponse>(
          withPrefix(
            `/api/banner?locale=${encodeURIComponent(langCode || "en-us")}`
          ),
          { timeout: 20000, headers: { "Cache-Control": "no-cache" } }
        )
        if (!cancelled) {
          const matchedBannersToLangCode = mapApiBannersToFinalBannerMessages(
            response.data.banners
          )
          setBannerMessages(matchedBannersToLangCode.flat())
        }
      } catch (error) {
        console.error("Failed to fetch site banners:", error)
      } finally {
        if (!cancelled) setHasFetched(true)
      }
    }

    fetchBanners()
    return () => {
      cancelled = true
    }
  }, [langCode])

  const handleCrossIconClick = useCallback(
    (bannerMessageKey: string) => {
      setBannerMessages((bannerMessages) => {
        const filtered = bannerMessages.filter((x) => {
          if (x && x.key === bannerMessageKey) {
            return false
          }

          return true
        })

        return filtered
      })
    },
    [bannerMessages]
  )

  if (!hasFetched || bannerMessages.length === 0) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      {bannerMessages?.length > 0
        ? bannerMessages.map((banner) => {
            if (
              !banner?.key ||
              banner?.key?.trim()?.toLowerCase() === "placeholder message"
            ) {
              return null
            }

            const callToAction = renderEnhancedRichText(
              banner?.callToAction?.raw ?? undefined
            )
            const siteBannerMessage = renderEnhancedRichText(
              banner?.siteBannerMessage?.raw ?? undefined,
              enhancedCustomRendererToRemovePTag
            )
            const siteBannerHeader = banner?.siteBannerHeader

            const callToActionRaw = banner?.callToAction?.raw
            const callToActionUrl =
              callToActionRaw && callToActionRaw.length > 0
                ? JSON.parse(callToActionRaw)?.content?.find(
                    (x: { nodeType: string }) => x.nodeType === "hyperlink"
                  )?.data?.uri
                : ""

            return (
              <SingleBannerMessage
                key={banner.key}
                bannerMessageKey={banner.key}
                handleCrossIconClick={handleCrossIconClick}
                callToAction={callToAction}
                siteBannerMessage={siteBannerMessage}
                siteBannerHeader={siteBannerHeader}
                callToActionUrl={callToActionUrl}
              />
            )
          })
        : null}
    </div>
  )
}

type SingleBannerMessageProps = {
  handleCrossIconClick: (siteBannerMessage: string) => void
  callToAction: React.ReactNode
  siteBannerMessage: React.ReactNode
  bannerMessageKey: string
  callToActionUrl: string
  siteBannerHeader?: string | React.ReactNode
}

const SingleBannerMessage: FC<SingleBannerMessageProps> = ({
  callToAction,
  siteBannerMessage,
  bannerMessageKey,
  handleCrossIconClick,
  callToActionUrl,
  siteBannerHeader
}) => {
  const handleClick = useCallback(() => {
    handleCrossIconClick(bannerMessageKey)
  }, [callToAction, siteBannerMessage, handleCrossIconClick])

  return (
    <div className={styles.container}>
      <div className={styles.messageContainer}>
        {siteBannerHeader && (
          <div className={styles.titleContainer}>
            <Typography
              variant="heading-xs"
              color="Neutral 600"
              className={styles.title}
            >
              {siteBannerHeader}
            </Typography>
          </div>
        )}
        {siteBannerMessage && (
          <Typography variant="heading-xs">{siteBannerMessage}</Typography>
        )}
      </div>

      <div className={styles.callToActionContainer}>
        <span
          onClick={(e) => {
            events?.bannerClick(callToActionUrl, SingleBannerMessage?.name)
          }}
        >
          {callToAction}
        </span>
      </div>

      <Icon
        iconProps={{
          onClick: handleClick
        }}
        className={styles.crossIcon}
        icon={<CloseIcon size="small" />}
      />
    </div>
  )
}

export default Banner
